import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  AppBar,
  Box,
  BottomNavigation,
  BottomNavigationAction,
  Button,
  CircularProgress,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  Link,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import WhatshotIcon from "@mui/icons-material/Whatshot";
import PersonIcon from "@mui/icons-material/Person";
import BuildIcon from "@mui/icons-material/Build";
import MenuIcon from "@mui/icons-material/Menu";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import AuthContext from "../context/AuthContext";

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
const apiver = "api/v3";

const feedOptions = [
  { label: "Latest", value: "latest", icon: <HistoryIcon fontSize="small" /> },
  { label: "Popular", value: "popular", icon: <WhatshotIcon fontSize="small" /> },
];

const drawerWidth = 260;

const navItems = [
  { id: "history", label: "History", icon: <HistoryIcon /> },
  { id: "profile", label: "Profile", icon: <PersonIcon /> },
  { id: "other", label: "Other", icon: <BuildIcon /> },
];

const ProFeatures = () => {
  const [feed, setFeed] = useState("latest");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activePanel, setActivePanel] = useState("history");
  const [cancelState, setCancelState] = useState({
    loading: false,
    error: "",
    success: "",
  });
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const { tokenPair, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));
  const headerOffset = theme.spacing(4);

  const isProUser = user?.plan?.startsWith("pro");
  const endpoint = useMemo(
    () => `${apiBaseUrl}/${apiver}/pro/history/${feed}`,
    [feed]
  );
  const profileEndpoint = `${apiBaseUrl}/auth/trial/cancel`;

  const formatCreatedAt = (createdAt) => {
    const rawDate = createdAt?.$date ?? createdAt;
    if (!rawDate) return "Unknown date";
    const date = new Date(rawDate);
    if (Number.isNaN(date.getTime())) return rawDate;
    return new Intl.DateTimeFormat("en", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(date);
  };

  const formatYears = (yearRange) => {
    if (!Array.isArray(yearRange) || yearRange.length !== 2) return "";
    const [start, end] = yearRange;
    return `${start ?? "?"} – ${end ?? "?"}`;
  };

  const formatHitCount = (hits) =>
    `${hits ?? 0} hit${hits === 1 ? "" : "s"}`;

  const replaySearch = (params) => {
    if (!params) return;
    navigate("/", { state: { historySearch: { params } } });
  };

  useEffect(() => {
    const fetchHistory = async () => {
      if (!isProUser) return;

      setLoading(true);
      setError("");
      try {
        const headers = tokenPair?.access
          ? { Authorization: `Bearer ${tokenPair.access}` }
          : {};
        const res = await axios.get(endpoint, { headers });
        setItems(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        setItems([]);
        setError(
          err.response?.status === 401
            ? "You need to be signed in with a Pro subscription to view this history."
            : "Unable to load history right now. Please try again."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [endpoint, isProUser, tokenPair]);

  if (!isProUser) {
    return (
      <Box textAlign="center" py={10}>
        <Typography variant="h4" gutterBottom>
          Pro Features
        </Typography>
        <Typography variant="body1">
          Sign in with a Pro subscription to access history, profile tools, and upcoming features.
        </Typography>
      </Box>
    );
  }

  const handleCancel = async () => {
    setCancelState({ loading: true, error: "", success: "" });
    try {
      const headers = tokenPair?.access
        ? { Authorization: `Bearer ${tokenPair.access}` }
        : {};
      await axios.post(profileEndpoint, null, { headers });
      setCancelState({
        loading: false,
        error: "",
        success: "Your premium service cancellation request has been submitted.",
      });
    } catch (err) {
      setCancelState({
        loading: false,
        error:
          err.response?.status === 401
            ? "You need to be signed in to cancel your subscription."
            : "Unable to cancel right now. Please try again.",
        success: "",
      });
    }
  };

  const renderHistoryPanel = () => (
    <Box maxWidth="md" mx="auto">
      <Stack direction="row" alignItems="center" spacing={2} mb={3}>
        <Typography variant="h4" sx={{ flexGrow: 1 }}>
          Pro History
        </Typography>
        <FormControl size="small" sx={{ minWidth: 160, ml: 3 }}>
          <InputLabel id="history-feed-label">Feed</InputLabel>
          <Select
            labelId="history-feed-label"
            label="Feed"
            value={feed}
            onChange={(e) => setFeed(e.target.value)}
          >
            {feedOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <Stack direction="row" spacing={1} alignItems="center">
                  {option.icon}
                  <span>{option.label}</span>
                </Stack>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Stack alignItems="center" py={6}>
          <CircularProgress color="primary" />
        </Stack>
      ) : items.length === 0 ? (
        <Typography variant="body1">
          No {feed === "latest" ? "recent" : "popular"} searches found yet.
        </Typography>
      ) : (
        <Stack spacing={2}>
          {items.map((item) => (
            <Paper key={item.sig} variant="outlined" sx={{ p: 2 }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
              >
                <Link
                  component="button"
                  onClick={() => replaySearch(item.params)}
                  underline="hover"
                  variant="subtitle1"
                >
                  {feed === "latest"
                    ? formatCreatedAt(item.createdAt)
                    : formatHitCount(item.hits)}
                </Link>
                <Typography variant="body2" color="text.secondary">
                  {item.params?.query ? `${item.params.query} • ` : ""}
                  {item.params?.genre?.length
                    ? item.params.genre.join(", ")
                    : "Any genre"}
                  {" • "}
                  {formatYears(item.params?.year)}
                </Typography>
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}
    </Box>
  );

  const renderProfilePanel = () => (
    <Box maxWidth="sm" mx="auto">
      <Typography variant="h4" gutterBottom>
        Account Profile
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Manage your premium subscription status and account preferences.
      </Typography>
      {cancelState.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {cancelState.error}
        </Alert>
      )}
      {cancelState.success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {cancelState.success}
        </Alert>
      )}
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Typography variant="h6">Premium Subscription</Typography>
          <Typography variant="body2" color="text.secondary">
            Canceling will end your premium service at the end of your current billing period.
          </Typography>
          <Button
            variant="contained"
            color="error"
            onClick={handleCancel}
            disabled={cancelState.loading}
          >
            {cancelState.loading ? "Processing..." : "Cancel Premium"}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );

  const renderOtherPanel = () => (
    <Box maxWidth="sm" mx="auto">
      <Typography variant="h4" gutterBottom>
        Coming Soon
      </Typography>
      <Alert severity="info">This feature is currently under maintenance.</Alert>
    </Box>
  );

  const navList = (
    <Box sx={{ width: drawerWidth }}>
      <Toolbar>
        <Typography variant="h6" noWrap>
          Pro Features
        </Typography>
      </Toolbar>
      <List>
        {navItems.map((item) => (
          <ListItemButton
            key={item.id}
            selected={activePanel === item.id}
            onClick={() => {
              setActivePanel(item.id);
              setMobileDrawerOpen(false);
            }}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText primary={item.label} />
          </ListItemButton>
        ))}
      </List>
    </Box>
  );

  const activeLabel = navItems.find((item) => item.id === activePanel)?.label;

  const showTabletNav = !isMdUp && !isSmDown;
  const contentTopPadding = isSmDown ? 6 : showTabletNav ? 10 : 8;

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {showTabletNav && (
        <AppBar position="fixed" sx={{ top: headerOffset }}>
          <Toolbar>
            <IconButton
              color="inherit"
              edge="start"
              onClick={() => setMobileDrawerOpen(true)}
              sx={{ mr: 1 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap>
              {activeLabel ?? "Pro Features"}
            </Typography>
          </Toolbar>
        </AppBar>
      )}

      {isMdUp && (
        <Drawer
          variant="permanent"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
              top: headerOffset,
              height: `calc(100% - ${headerOffset})`,
            },
          }}
        >
          {navList}
        </Drawer>
      )}

      {showTabletNav && (
        <Drawer
          variant="temporary"
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
              top: headerOffset,
              height: `calc(100% - ${headerOffset})`,
            },
          }}
        >
          {navList}
        </Drawer>
      )}

      <Box
        component="main"
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          pt: contentTopPadding,
          pb: isSmDown ? 8 : 4,
        }}
      >
        <Box sx={{ p: { xs: 2, md: 3 }, flex: 1 }}>
          {activePanel === "history" && renderHistoryPanel()}
          {activePanel === "profile" && renderProfilePanel()}
          {activePanel === "other" && renderOtherPanel()}
        </Box>
      </Box>

      {isSmDown && (
        <Box sx={{ position: "fixed", bottom: 0, left: 0, right: 0 }}>
          <BottomNavigation
            showLabels
            value={navItems.findIndex((item) => item.id === activePanel)}
            onChange={(_, newValue) => setActivePanel(navItems[newValue].id)}
          >
            {navItems.map((item) => (
              <BottomNavigationAction
                key={item.id}
                label={item.label}
                icon={item.icon}
              />
            ))}
          </BottomNavigation>
        </Box>
      )}
    </Box>
  );
};

export default ProFeatures;
